import { z } from 'zod';
import { idParamSchema, moneySchema, nameAndCode, optionalString } from './common';
import { reportKindSchema } from './enums';

const labTestFields = {
  ...nameAndCode,
  price: moneySchema,
  /**
   * Committed turnaround in hours, capped at 30 days.
   *
   * Null means the test has no committed turnaround, and a report for it is
   * never counted as overdue.
   */
  turnaround_hours: z.coerce.number().int().min(1).max(720).nullish(),
  comment: optionalString(),
  /**
   * What the patient must do before the sample is taken.
   *
   * Printed on the registration slip of every visit that books this test,
   * de-duplicated across the visit's tests — so it is written to be read by a
   * patient, not by the bench. `TestProcedure` holds the SOP.
   */
  sample_instructions: optionalString(),
  /** The bench that runs this. Groups the printed rate list; optional everywhere else. */
  department_id: idParamSchema.nullish(),
  /**
   * The reporting doctor copied onto a new report at registration.
   *
   * A default rather than a rule: the report keeps its own `consultant_id`, so
   * changing this later cannot rewrite who signed a report last month.
   */
  default_consultant_id: idParamSchema.nullish(),
};

/**
 * Parameters are referenced by id and linked with Prisma's `set`, so editing a
 * lab test re-links existing parameters. The previous update built brand-new
 * TestParameter instances and saved them through a cascading many-to-many,
 * which inserted duplicate parameter rows on every edit.
 */
export const createLabTestSchema = z
  .object({
    ...labTestFields,
    report_kind: reportKindSchema.default('parameters'),
    parameter_ids: z.array(idParamSchema).default([]),
    /** A culture's default sensitivity panel, in id order. */
    antibiotic_ids: z.array(idParamSchema).default([]),
  })
  // A culture records an organism and antibiotics, never parameter rows, so
  // each kind requires its own list and the other must be empty.
  .superRefine((input, context) => {
    const culture = input.report_kind === 'culture';
    if (!culture && input.parameter_ids.length === 0) {
      context.addIssue({
        code: 'custom',
        path: ['parameter_ids'],
        message: 'Select at least one parameter',
      });
    }
    if (culture && input.parameter_ids.length > 0) {
      context.addIssue({
        code: 'custom',
        path: ['parameter_ids'],
        message: 'A culture test has no parameters',
      });
    }
    if (culture && input.antibiotic_ids.length === 0) {
      context.addIssue({
        code: 'custom',
        path: ['antibiotic_ids'],
        message: 'Select at least one antibiotic',
      });
    }
    if (!culture && input.antibiotic_ids.length > 0) {
      context.addIssue({
        code: 'custom',
        path: ['antibiotic_ids'],
        message: 'Only a culture test has an antibiotic panel',
      });
    }
  });
export type CreateLabTestInput = z.infer<typeof createLabTestSchema>;

export const updateLabTestSchema = createLabTestSchema;
export type UpdateLabTestInput = z.infer<typeof updateLabTestSchema>;
